/**
 * Flow approval pending inbox heuristics (L2-UI-AP-01 · H-04)
 *
 * Client-side filter on active assignments — owner/reviewer roles (Pending Approval stage).
 */

import type { TaskAssignment } from '@shared/flow/task-assignment-types';

export const FLOW_ASSIGNMENT_QUEUE_APPROVAL = 'approval' as const;

export type FlowApprovalPendingRole = Extract<TaskAssignment['assigneeRole'], 'owner' | 'reviewer'>;

export const APPROVAL_PENDING_ROLES: readonly FlowApprovalPendingRole[] = ['owner', 'reviewer'];

const APPROVAL_MARKERS = [
  'approval',
  'approve',
  'signoff',
  'sign-off',
  'review',
  '승인',
  '검토',
  '결재',
] as const;

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function matchesMarker(haystack: string): boolean {
  const normalized = normalize(haystack);
  return APPROVAL_MARKERS.some((marker) => normalized.includes(marker));
}

export interface ApprovalQueueAssignmentLike {
  flowId: string;
  status?: string;
  assigneeRole?: TaskAssignment['assigneeRole'];
  tags?: string[];
  name?: string;
}

export function isApprovalPendingRole(
  role: TaskAssignment['assigneeRole'] | undefined
): role is FlowApprovalPendingRole {
  return role === 'owner' || role === 'reviewer';
}

/** Active assignment where current user acts as approver (owner) or reviewer */
export function isApprovalPendingAssignment(row: ApprovalQueueAssignmentLike): boolean {
  if ((row.status ?? 'active') !== 'active') {
    return false;
  }
  if (row.assigneeRole && !isApprovalPendingRole(row.assigneeRole)) {
    return false;
  }
  if (isApprovalPendingRole(row.assigneeRole)) {
    return true;
  }
  if (matchesMarker(row.flowId)) {
    return true;
  }
  if (row.name && matchesMarker(row.name)) {
    return true;
  }
  if (row.tags?.some((tag) => matchesMarker(tag))) {
    return true;
  }
  return false;
}

export function filterApprovalPendingAssignments<T extends ApprovalQueueAssignmentLike>(
  rows: T[]
): T[] {
  return rows.filter(isApprovalPendingAssignment);
}

export function resolveApprovalQueueRoleLabel(
  role: TaskAssignment['assigneeRole'] | undefined
): '승인' | '검토' | '—' {
  if (role === 'owner') return '승인';
  if (role === 'reviewer') return '검토';
  return '—';
}
